import { useEffect, useState, type ReactNode } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useIsMobile } from "@/hooks/use-mobile";

import { attendanceErrorText } from "./attendanceApi";

/** Every change asks for a reason; a dialog on desktop, a drawer on the phone. */
export const ReasonDialog = ({
  open,
  onOpenChange,
  title,
  description,
  submitLabel,
  canSubmit = true,
  onSubmit,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  submitLabel: string;
  canSubmit?: boolean;
  onSubmit: (reason: string) => Promise<unknown>;
  children?: ReactNode;
}) => {
  const isMobile = useIsMobile();
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    if (open) {
      setReason("");
      setError(null);
    }
  }, [open]);

  const submit = async () => {
    if (!reason.trim()) {
      setError("Bitte einen Grund angeben.");
      return;
    }
    setPending(true);
    setError(null);
    try {
      await onSubmit(reason.trim());
      onOpenChange(false);
    } catch (e) {
      setError(attendanceErrorText(e));
    } finally {
      setPending(false);
    }
  };

  const body = (
    <div className="flex flex-col gap-4">
      {children}
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="attendance-reason">Grund</Label>
        <Textarea
          id="attendance-reason"
          value={reason}
          required
          aria-invalid={!!error && !reason.trim()}
          onChange={(e) => setReason(e.target.value)}
          placeholder="z. B. Stand an der Tür, QR ging nicht"
        />
      </div>
      {error ? (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
  const actions = (
    <>
      <Button variant="outline" onClick={() => onOpenChange(false)}>
        Abbrechen
      </Button>
      <Button onClick={submit} disabled={pending || !canSubmit}>
        {submitLabel}
      </Button>
    </>
  );

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent>
          <DrawerHeader className="text-left">
            <DrawerTitle>{title}</DrawerTitle>
            {description ? (
              <DrawerDescription>{description}</DrawerDescription>
            ) : null}
          </DrawerHeader>
          <div className="px-4">{body}</div>
          <DrawerFooter>{actions}</DrawerFooter>
        </DrawerContent>
      </Drawer>
    );
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description ? (
            <DialogDescription>{description}</DialogDescription>
          ) : null}
        </DialogHeader>
        {body}
        <DialogFooter>{actions}</DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
